"use client";

import CloseIcon from "@repo/ui-system/icons/action/close/outline.svg";
import { Dialog } from "radix-ui";

import type { BestProduct } from "@/lib/api/best/keywords/schema";
import { cn } from "@/lib/utils";

import { usePrimeDayPopup } from "./hooks/use-prime-day-popup";
import { PopupDesktop } from "./popup/popup-desktop";
import { PopupMobile } from "./popup/popup-mobile";

type Props = {
  product: BestProduct;
};

export function PrimeDayPopup({ product }: Props) {
  const { isOpen, close, handleClickOut } = usePrimeDayPopup();

  const productImage = product.images[0]?.url ?? "";
  const dealUrl = product.stores[0]?.url ?? "";

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => !open && close()}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-modal bg-[rgba(21,20,20,0.65)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2",
            "w-[300px] lg:w-[640px] max-w-[calc(100%-40px)] rounded-2xl bg-white shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
          )}
        >
          <Dialog.Description className="sr-only">
            Prime Day promotional offer for {product.name}.
          </Dialog.Description>

          <Dialog.Close className="absolute top-3 right-3 z-20 flex items-center justify-center size-7 rounded-full lg:hover:bg-black/10 lg:transition-colors">
            <CloseIcon
              className="size-6 text-[#1a1c23]"
              aria-hidden="true"
            />
            <span className="sr-only">Close</span>
          </Dialog.Close>

          {/* Mobile popup */}
          <div className="relative lg:hidden">
            {product.percentOff && (
              <span className="absolute -top-3.5 left-7 z-10 rounded-lg bg-[#e03223] px-2 py-1 text-sm font-medium text-grayscale-white">
                {product.percentOff}% Off
              </span>
            )}
            <PopupMobile
              productUrl={dealUrl}
              productName={product.name}
              productImage={productImage}
              onClickOut={handleClickOut}
            />
          </div>

          {/* Desktop popup */}
          <div className="hidden lg:block">
            <PopupDesktop
              productUrl={dealUrl}
              productName={product.name}
              productImage={productImage}
              percentOff={product.percentOff}
              onClickOut={handleClickOut}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
